import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Building2, Search, Star } from 'lucide-react';

interface HotelAccount {
  id: string;
  email: string;
  hotel_name: string;
  category: string;
  city: string;
  country: string;
  registration_number: string;
  created_at: string;
  updated_at: string;
  total_integrations: number;
}

export function HotelAccounts() {
  const [accounts, setAccounts] = useState<HotelAccount[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const { data, error: queryError } = await supabase
        .from('hotel_accounts_secure')
        .select('*')
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      // Ensure total_integrations is 0 when null
      const processedData = (data || []).map(account => ({
        ...account,
        total_integrations: account.total_integrations || 0
      }));

      setAccounts(processedData);
    } catch (err) {
      setError('Failed to load hotel accounts');
      console.error('Error loading accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(account => 
    account.hotel_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.registration_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderStars = (category: string) => {
    const stars = [];
    const numStars = parseInt(category, 10);
    
    for (let i = 0; i < numStars; i++) {
      stars.push(
        <Star 
          key={i} 
          className="h-4 w-4 text-yellow-400 inline-block" 
          fill="currentColor"
        />
      );
    }
    
    return (
      <div className="flex items-center space-x-0.5">
        {stars}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="text-gray-500">Loading hotel accounts...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center space-x-2">
          <Building2 className="h-6 w-6 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-900">Hotel Accounts</h2>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50">
            {error}
          </div>
        )}

        <div className="mb-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by hotel name, email, or registration number"
                  className="form-input pl-10"
                />
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Hotel Name</th>
                <th className="table-header">Category</th>
                <th className="table-header">Email</th>
                <th className="table-header">Location</th>
                <th className="table-header">Registration</th>
                <th className="table-header">Created</th>
                <th className="table-header">Integrations</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filteredAccounts.map((account) => (
                <tr key={account.id} className="table-row">
                  <td className="table-cell">
                    {account.hotel_name}
                  </td>
                  <td className="table-cell">
                    {renderStars(account.category)}
                  </td>
                  <td className="table-cell">
                    {account.email}
                  </td>
                  <td className="table-cell">
                    {account.city}, {account.country}
                  </td>
                  <td className="table-cell">
                    {account.registration_number}
                  </td>
                  <td className="table-cell">
                    {new Date(account.created_at).toLocaleDateString()}
                  </td>
                  <td className="table-cell">
                    <span className={`status-badge ${
                      account.total_integrations > 0
                        ? 'status-active'
                        : 'status-inactive'
                    }`}>
                      {account.total_integrations}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredAccounts.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {searchTerm
                  ? 'No accounts found matching your search criteria.'
                  : 'No hotel accounts found.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}